import CheckIcon from '@mui/icons-material/Check';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridPanel,
  GridPanelProps,
  GridToolbar,
  useGridApiContext,
  useGridApiRef
} from '@mui/x-data-grid';
import { useMutation, useQuery } from '@tanstack/react-query';
import { format, isSameMonth, parseISO } from 'date-fns';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, PlusCircle, XClose } from 'untitledui-js-base';
import { useCommonApi } from '../api';
import { onSuccessCustomApiResponse } from '../api/utils';
import { PATHS } from '../paths';
import { surfaceSx } from '../styles/tokens';
import { formatDateWithShortWeekday } from '../utils/formatDateWithShortWeekday';
import { PageLayout, RouterLink } from './common';
import { DateRangeToolbar } from './common/DateRangeToolbar';
import { AddNewStatementDialog } from './dealerStatement/AddNewStatementDialog';
import { ChangeAccidentStateDialog } from './dealerStatement/ChangeAccidentStateDialog';
import { HideStatementDialog } from './dealerStatement/HideStatementDialog';
import { useGridColumnPreferences } from './dealerStatement/hooks/useGridColumnPreferences';
import { useGridStateUrlSync } from './dealerStatement/hooks/useGridStateUrlSync';
import {
  PAYMENT_FIELD,
  PAYMENT_VALUES,
  STATUS_VALUES,
  buildQuickFilterItems,
  getPaymentFilterValue,
  isQuickFilterItem
} from './dealerStatement/utils/quickFilters';
import { useAuth } from './hooks';
import { useDateRange } from './hooks/useDateRange';
import { RegistryType } from './types/accident';
import { RegistryStatement, StatementRequest } from './types/statement';
import { DealerStatement, StatementRowType } from './types/statistics';

interface TotalItemProps {
  title: string;
  value: number | string;
  isLoading?: boolean;
}

export const TotalItem = ({ title, value, isLoading }: TotalItemProps) => (
  <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
    <Stack justifyContent="space-between" gap={1.5} sx={{ ...surfaceSx, width: 1, height: 1, padding: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3 }}>
        {title}
      </Typography>
      {isLoading ? (
        <Skeleton variant="rounded" width={64} height={32} />
      ) : (
        <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </Typography>
      )}
    </Stack>
  </Grid>
);

const useColumns = (rows: DealerStatement[]) =>
  useMemo<GridColDef<DealerStatement>[]>(
    () => [
      {
        field: '__rowNumber__',
        headerName: '#',
        width: 15,
        sortable: false,
        filterable: false,
        headerAlign: 'center',
        align: 'center',
        headerClassName: 'theme--White col--rowNumber',
        cellClassName: 'col--rowNumber',
        renderCell: (params) => {
          const { api, id } = params;
          const idx = api.getRowIndexRelativeToVisibleRows(id);
          return idx + 1;
        }
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Detail',
        width: 60,
        getActions: (params) => {
          if (params.row.isRejected || params.row.type === 'custom') return [];
          return [
            <GridActionsCellItem
              icon={
                <RouterLink to={PATHS.ACCIDENTS(params.row.id)}>
                  <IconButton color="info">
                    <Eye />
                  </IconButton>
                </RouterLink>
              }
              label="Detail"
              onClick={(e) => {
                e.stopPropagation();
              }}
              showInMenu={false}
            />
          ];
        }
      },
      {
        field: 'statementStatusLabel',
        headerName: 'Status',
        valueGetter: (_, row) => row.statementStatusLabel,
        width: 100,
        headerClassName: 'theme--White',
        filterable: true
      },
      {
        field: PAYMENT_FIELD,
        headerName: 'Stav úhrady',
        valueGetter: (_, row) => getPaymentFilterValue(row),
        width: 100,
        headerClassName: 'theme--White',
        filterable: true,
        hideable: false,
        disableColumnMenu: true
      },
      {
        field: 'date',
        headerName: 'Datum',
        width: 180,
        type: 'date',
        valueGetter: (_, row) => (row.date ? new Date(row.date) : null),
        valueFormatter: (_, row) => {
          if (!row.date) return '';
          const date = new Date(row.date);
          return Number.isNaN(date.getTime()) ? '' : formatDateWithShortWeekday(row.date);
        },
        filterable: true,
        headerClassName: 'theme--White',
        renderCell: (params) => {
          const { row, api, id } = params;

          if (!row.date) return null;

          const rowDate = new Date(row.date);
          if (Number.isNaN(rowDate.getTime())) return null;

          const index = api.getRowIndexRelativeToVisibleRows(id);
          const thisDate = format(rowDate, 'yyyy-MM-dd');

          const prevId = api.getSortedRowIds()?.[index - 1];
          const prevRow = prevId ? api.getRow(prevId) : null;
          const prevDate =
            prevRow && prevRow.date
              ? (() => {
                  const pd = new Date(prevRow.date);
                  return Number.isNaN(pd.getTime()) ? null : format(pd, 'yyyy-MM-dd');
                })()
              : null;

          const showSeparator = thisDate !== prevDate;

          return (
            <Stack
              justifyContent="center"
              sx={{
                position: 'static',
                width: '100%',
                height: '100%',
                display: 'flex',
                pl: 1,
                '&::before': showSeparator
                  ? {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      height: '0',
                      width: '100%',
                      borderTop: '3px solid black',
                      zIndex: 1
                    }
                  : undefined
              }}
            >
              {row.date && !Number.isNaN(new Date(row.date).getTime()) && (
                <Typography fontSize={14}>{formatDateWithShortWeekday(row.date)}</Typography>
              )}
              {row.isTransferredStatement &&
                row.dateOriginal &&
                !Number.isNaN(new Date(row.dateOriginal).getTime()) && (
                  <Typography fontSize={10}>({formatDateWithShortWeekday(row.dateOriginal)})</Typography>
                )}
            </Stack>
          );
        }
      },
      {
        field: 'user',
        headerName: 'Jméno',
        type: 'string',
        width: 200,
        headerClassName: 'theme--White',
        valueGetter: (_, row) => `${row?.user?.surname} ${row?.user?.companyName}`,
        rowSpanValueGetter: (_, row) => `${row.user?.surname} ${row?.contractNumber}`,
        renderCell: (params) => {
          const { row } = params;
          if (!row.user) return null;
          return (
            <Stack direction="column" justifyContent="center" height={1} sx={{ lineHeight: 0.9 }}>
              {row.user.companyName && (
                <Typography component="span" fontSize={13} sx={{ display: 'block' }}>
                  {row.user.companyName}
                </Typography>
              )}
              <Typography component="span" fontSize={13}>
                {[row.user.surname, row.user.name].filter(Boolean).join(' ')}
              </Typography>
              {row.user.phone && (
                <Typography component="span" fontSize={10} fontStyle="italic">
                  Tel.: {row.user.phone}
                </Typography>
              )}
            </Stack>
          );
        }
      },
      {
        field: 'car',
        headerName: 'Auto',
        type: 'string',
        width: 150,
        headerClassName: 'theme--White',
        valueGetter: (_, row) => `${row?.car?.brand ?? ''} ${row?.car?.model ?? ''} ${row?.car?.plateNumber ?? ''}`,
        rowSpanValueGetter: (_, row) => `${row.user?.surname} ${row.contractNumber}`,
        renderCell: (params) => {
          const { row } = params;
          if (row.isRejected || !row.car) return '';
          return (
            <Stack direction="column" justifyContent="center" height={1}>
              <Typography component="span" fontSize={13}>
                {`${row.car.brand} ${row.car.model}`}
              </Typography>
              {row.car.plateNumber && (
                <Typography component="span" fontSize={11} fontStyle="italic">
                  {row.car.plateNumber}
                </Typography>
              )}
            </Stack>
          );
        }
      },
      {
        field: 'serviceSourceName',
        headerName: 'Servis',
        type: 'string',
        filterable: true,
        headerClassName: 'theme--White'
      },
      {
        field: 'contractNumber',
        headerName: 'Číslo smlouvy',
        width: 120,
        type: 'string',
        headerClassName: 'theme--White'
      },
      {
        field: 'documentNumber',
        headerName: 'Číslo faktury',
        width: 130,
        type: 'string',
        headerClassName: 'theme--White',
        valueGetter: (_, row) => {
          return row.documents?.map((d) => d.documentNumber).join(', ') ?? '';
        },
        renderCell: (params) => {
          const { row } = params;
          if (!row.documents || row.documents.length === 0) return '';
          return (
            <Stack direction="column" justifyContent="center" height={1} spacing={0.5}>
              {row.documents.map((doc, idx) => (
                <Typography key={idx} component="span" fontSize={13}>
                  {doc.documentNumber}
                </Typography>
              ))}
            </Stack>
          );
        }
      },
      {
        field: 'documentAmount',
        headerName: 'Částka',
        width: 100,
        type: 'string',
        headerClassName: 'theme--White',
        valueGetter: (_, row) => {
          return row.documents?.map((d) => d.amount).join(', ') ?? '';
        },
        renderCell: (params) => {
          const { row } = params;
          if (!row.documents || row.documents.length === 0) return '';
          return (
            <Stack direction="column" justifyContent="center" height={1} spacing={0.5}>
              {row.documents.map((doc, idx) => (
                <Typography key={idx} component="span" fontSize={13} textAlign="right">
                  {doc.amount}
                </Typography>
              ))}
            </Stack>
          );
        }
      },
      {
        field: 'paymentStatus',
        headerName: 'Stav',
        width: 150,
        type: 'string',
        headerClassName: 'theme--White',
        valueGetter: (_, row) => {
          return row.documents?.map((d) => d.paymentStatus.status).join(', ') ?? '';
        },
        renderCell: (params) => {
          const { row } = params;
          if (!row.documents || row.documents.length === 0) return '';
          return (
            <Stack direction="column" justifyContent="center" height={1} spacing={0.5}>
              {row.documents.map((doc, idx) => (
                <Stack
                  key={idx}
                  direction="column"
                  spacing={0.2}
                  sx={{
                    color: doc.paymentStatus.isPaid ? 'success.main' : 'error.main'
                  }}
                >
                  <Typography component="span" fontSize={13}>
                    {doc.paymentStatus.label}
                  </Typography>
                  {doc.paymentStatus.isPaid && doc.paymentStatus.isPaidDate && (
                    <Typography component="span" fontSize={11}>
                      {doc.paymentStatus.isPaidDate}
                    </Typography>
                  )}
                </Stack>
              ))}
            </Stack>
          );
        }
      },
      {
        field: 'paymentAmount',
        headerName: 'Výše úhrady',
        width: 120,
        type: 'string',
        headerClassName: 'theme--White',
        valueGetter: (_, row) => {
          return row.documents?.map((d) => d.paymentStatus.isPaidAmount || '-').join(', ') ?? '';
        },
        renderCell: (params) => {
          const { row } = params;
          if (!row.documents || row.documents.length === 0) return '';
          return (
            <Stack direction="column" justifyContent="center" height={1} spacing={0.5}>
              {row.documents.map((doc, idx) => (
                <Typography key={idx} component="span" fontSize={13} textAlign="right">
                  {doc.paymentStatus.isPaidAmount || '-'}
                </Typography>
              ))}
            </Stack>
          );
        }
      },
      {
        field: 'source',
        headerName: 'Zdroj',
        width: 120,
        headerClassName: 'theme--White',
        valueGetter: (_, row) => `${row.source?.label ?? ''} ${row.source?.name ?? ''}`,
        rowSpanValueGetter: (_, row) => `${row.user?.surname} ${row.contractNumber}`,
        renderCell: (params) => {
          const { row } = params;
          if (!row.source) return null;
          return (
            <Stack direction="column" justifyContent="center" height={1}>
              <Typography component="span" fontSize={13}>
                {row.source.label}
              </Typography>
              {row.source.name && (
                <Typography component="span" fontSize={11} fontStyle="italic">
                  ({row.source.name})
                </Typography>
              )}
            </Stack>
          );
        }
      },
      {
        field: 'commission',
        headerName: 'POJ',
        type: 'number',
        width: 90,
        headerClassName: 'theme--White',
        valueGetter: (_, row) => {
          if (row?.commission?.isCancelled) return 0; // zrušená POJ se počítá jako 0 Kč
          const value = row?.commission?.value;
          if (!value) return undefined;
          const numeric = Number(value.replace(/[^\d-]/g, ''));
          return Number.isNaN(numeric) ? undefined : numeric;
        },
        rowSpanValueGetter: (_, row) => `${row.user?.surname} ${row.contractNumber}`,
        renderCell: (params) => {
          const { row } = params;
          if (row.isRejected || !row.car) return '';
          if (row?.commission?.isCancelled) {
            return (
              <Stack direction="column" justifyContent="center" height={1}>
                <Typography component="span" fontSize={13} fontWeight={700} color="error">
                  ZRUŠENO
                </Typography>
                {row.commission.cancelledDate && (
                  <Typography component="span" fontSize={11} fontStyle="italic">
                    {row.commission.cancelledDate}
                  </Typography>
                )}
                {row.commission.cancelledBy && (
                  <Typography component="span" fontSize={11} fontStyle="italic">
                    {row.commission.cancelledBy}
                  </Typography>
                )}
              </Stack>
            );
          }
          return (
            <Stack direction="column" justifyContent="center" height={1}>
              <Typography component="span" fontSize={13}>
                {row?.commission?.value}
                {Boolean(row?.commission?.percentage) && ` (${row.commission.percentage} %)`}
              </Typography>
              {row?.commission?.date && (
                <Typography component="span" fontSize={11} fontStyle="italic">
                  {row?.commission?.date}
                </Typography>
              )}
            </Stack>
          );
        }
      },
      {
        field: 'v3Payments',
        headerName: 'Výplata',
        type: 'string',
        width: 90,
        headerClassName: 'theme--White',
        valueGetter: (_, row) =>
          row.v3Payments?.map((payment) => `${payment.month.padStart(2, '0')}/${payment.year}`).join(', ') ?? '',
        rowSpanValueGetter: (_, row) => `${row.user?.surname} ${row.contractNumber}`,
        renderCell: (params) => {
          const { row } = params;
          if (!row.v3Payments || row.v3Payments.length === 0) return '';
          return (
            <Stack direction="column" justifyContent="center" height={1}>
              {row.v3Payments.map((payment, idx) => (
                <Typography key={idx} component="span" fontSize={13}>
                  {payment.month.padStart(2, '0')}/{payment.year}
                </Typography>
              ))}
            </Stack>
          );
        }
      },
      {
        field: 'privateKm',
        headerName: 'Privátní km',
        headerClassName: 'theme--White',
        width: 80
      },
      {
        field: 'note',
        headerName: 'Poznámka',
        headerClassName: 'theme--White',
        width: 200,
        rowSpanValueGetter: (_, row) => `${row.user?.surname} ${row.contractNumber}`
      },
      {
        field: 'rejectionInfo',
        headerName: 'Důvod',
        valueGetter: (_, row) => row.rejectionInfo?.label,
        width: 200,
        headerClassName: 'theme--White',
        renderCell: (params) => {
          const { row } = params;
          return (
            <Stack direction="column" justifyContent="center" height={1}>
              <>
                {row.rejectionInfo && (
                  <>
                    <Typography component="span" fontSize={13}>
                      {row.rejectionInfo.label}
                    </Typography>
                    {row.rejectionInfo.reason && (
                      <Typography component="span" fontSize={12} fontStyle="italic">
                        ({row.rejectionInfo.reason})
                      </Typography>
                    )}
                  </>
                )}
                {row.dealerStatementNote && (
                  <Typography component="span" fontSize={11} fontStyle="italic">
                    ({row.dealerStatementNote})
                  </Typography>
                )}
              </>
            </Stack>
          );
        }
      }
    ],
    [rows]
  );

const MobileGridPanel = ({ children, ...props }: GridPanelProps) => {
  const apiRef = useGridApiContext();

  return (
    <GridPanel {...props}>
      <Box sx={{ position: 'relative', width: '100%' }}>
        <IconButton
          size="small"
          aria-label="Zavřít"
          onClick={() => apiRef.current.hidePreferences()}
          sx={{
            display: { xs: 'inline-flex', sm: 'none' },
            position: 'absolute',
            top: 4,
            right: 4,
            zIndex: 1
          }}
        >
          <XClose size="18px" />
        </IconButton>
        {children}
      </Box>
    </GridPanel>
  );
};

const DealerStatementPageContent = () => {
  const [openStatementDialog, setOpenStatementDialog] = useState(false);
  const [statementDialogData, setStatementDialogData] = useState<StatementRequest | undefined>(undefined);
  const [hideStatementDialogId, setHideStatementDialogId] = useState<number | undefined>(undefined);
  const [rejectDealerStatementId, setRejectDealerStatementId] = useState<number | undefined>(undefined);
  const [isRejectedByDealerStatement, setIsRejectedByDealerStatement] = useState<boolean>(false);

  const { user } = useAuth();
  const navigate = useNavigate();

  const { range: dateRange, setCustom } = useDateRange({
    weekStartsOn: 1,
    initialPreset: 'thisMonth',
    fmt: 'yyyy-MM-dd'
  });

  /* Custom quick filters */
  const apiRef = useGridApiRef();
  const {
    isReady: isPrefsReady,
    initialState: prefsInitialState,
    onColumnVisibilityModelChange,
    onColumnWidthChange,
    onColumnOrderChange
  } = useGridColumnPreferences(apiRef);
  const [selected, setSelected] = useState<string[]>([]);

  useGridStateUrlSync({
    apiRef,
    selected,
    setSelected,
    dateRange,
    setCustom,
    replaceHistory: true,
    debounceMs: 300
  });

  const applyChips = (chips: string[]) => {
    const currentModel = apiRef.current.state.filter.filterModel;
    const otherItems = currentModel.items.filter((item) => !isQuickFilterItem(item));
    apiRef.current.setFilterModel({
      ...currentModel,
      items: [...buildQuickFilterItems(chips), ...otherItems]
    });
  };

  const toggle = (val: string) => {
    const next = selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val];
    setSelected(next);
    applyChips(next);
  };

  const clear = () => {
    setSelected([]);
    applyChips([]);
  };

  const { mutate: addNewStatement, isPending: isSavingStatement } = useMutation(useCommonApi().addNewStatement());
  const { mutate: updateStatement, isPending: isUpdatingStatement } = useMutation(
    useCommonApi().updateCustomStatement()
  );
  const { mutate: removeStatement, isPending: isRemovingStatement } = useMutation(
    useCommonApi().removeCustomStatement()
  );
  const { mutate: hideDealerStatementAccident, isPending: isHidingDealerStatementAccident } = useMutation(
    useCommonApi().hideDealerStatementAccident()
  );
  const { mutate: rejectDealerStatement, isPending: isChangingStatementStatus } = useMutation(
    useCommonApi().rejectDealerStatementAccident()
  );

  // PHM price logic
  const [phmPriceValue, setPhmPriceValue] = useState('');
  const [phmLittersValue, setPhmLittersValue] = useState('');
  const { mutate: savePhmPrice, isPending: isSavingPhmPrice } = useMutation(useCommonApi().setDealerAvgPhmPrice());

  // Parse dateRange to get year for incomes query
  const dateFromParsed = parseISO(dateRange[0]);
  const dateToParsed = parseISO(dateRange[1]);
  const incomesYear = dateFromParsed.getFullYear().toString();

  // Check if date range is within single month
  const isSingleMonth = isSameMonth(dateFromParsed, dateToParsed);
  const selectedMonth = isSingleMonth ? (dateFromParsed.getMonth() + 1).toString().padStart(2, '0') : null;

  // Get saved PHM price and liters from user data
  const savedPhmPrice = user?.avgPhmPrices?.[incomesYear]?.[selectedMonth ?? ''];
  const savedPhmLiters = user?.phmLiters?.[incomesYear]?.[selectedMonth ?? ''];

  // Update PHM input when saved price/liters change
  useEffect(() => {
    if (savedPhmPrice != null) {
      setPhmPriceValue(String(savedPhmPrice));
    } else {
      setPhmPriceValue('');
    }
    if (savedPhmLiters != null) {
      setPhmLittersValue(String(savedPhmLiters));
    } else {
      setPhmLittersValue('');
    }
  }, [savedPhmPrice, savedPhmLiters, selectedMonth]);

  const handleSavePhmPrice = () => {
    if (!selectedMonth) return;
    const parsedPrice = Number.parseFloat(phmPriceValue.replace(',', '.'));
    const parsedLitters = Number.parseFloat(phmLittersValue.replace(',', '.'));
    if (Number.isNaN(parsedPrice)) return;
    savePhmPrice({
      year: incomesYear,
      month: selectedMonth,
      price: parsedPrice,
      liters: Number.isNaN(parsedLitters) ? undefined : parsedLitters
    });
  };

  const { data: dealerStatementReport, isPending: isLoadingData } = useQuery(
    useCommonApi().getDealerStatementStatistics({
      dateFrom: dateRange[0],
      dateTo: dateRange[1],
      dealerId: user?.id as string
    })
  );
  const { data: statementTypes } = useQuery<RegistryStatement[]>(
    useCommonApi().getRegistryList(RegistryType.STATEMENT_TYPES)
  );

  const rows = useMemo(() => dealerStatementReport?.results ?? [], [dealerStatementReport]);
  const columns = useColumns(rows);

  const rowStyles = `
  .row-separator {
    border-top: 3px solid black !important;
  }
  
  .relative {
    position: relative;
  }

  ${rows.map((row) => `.row-color-${row.id} { background-color: ${row.processingColor}!important; }`).join('\n')}
`;

  const printStyles = `
  @media print {
    @page {
      size: landscape;
    }

    body {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      margin: 0;
      padding: 0;
    }

    .MuiDataGrid-root {
      font-size: 10px !important;
      width: 100% !important;
      overflow: visible !important;
    }

    .MuiDataGrid-columnHeaders,
    .MuiDataGrid-cell {
      font-size: 10px !important;
      white-space: normal !important;
    }

    .MuiDataGrid-virtualScroller,
    .MuiDataGrid-main,
    .MuiDataGrid-window {
      overflow: visible !important;
      height: auto !important;
    }

    .MuiDataGrid-toolbarContainer,
    .MuiDataGrid-footerContainer {
      display: none !important;
    }

    table, tr, td, th,
    .MuiDataGrid-row {
      page-break-inside: avoid !important;
    }

    /* Fixní a úzké číslo řádku v tisku */
    .col--rowNumber {
      min-width: 40px !important;
      max-width: 40px !important;
      width: 40px !important;
    }
  }
`;

  const handleCloseStatement = () => {
    setOpenStatementDialog(false);
    setStatementDialogData(undefined);
  };

  const handleAddNewStatement = (data: StatementRequest) => {
    addNewStatement(
      { statementData: data },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: handleCloseStatement
        })
      }
    );
  };

  const handleUpdateStatement = (data: StatementRequest) => {
    updateStatement(
      { statementId: statementDialogData?.id as number, statementData: data },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: handleCloseStatement
        })
      }
    );
  };

  const handleRemoveStatement = () => {
    removeStatement(
      { statementId: statementDialogData?.id as number },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: handleCloseStatement
        })
      }
    );
  };

  const handleRejectDealerStatement = (note?: string) => {
    if (!!rejectDealerStatementId)
      rejectDealerStatement(
        { statementId: rejectDealerStatementId, note },
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              setRejectDealerStatementId(undefined);
              setIsRejectedByDealerStatement(false);
            }
          })
        }
      );
  };

  const handleHideStatement = () => {
    const id = rejectDealerStatementId ?? hideStatementDialogId;
    if (id == null) return;
    hideDealerStatementAccident(
      { statementId: id },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: () => {
            setRejectDealerStatementId(undefined);
            setHideStatementDialogId(undefined);
          }
        })
      }
    );
  };

  const handleRowClick: GridEventListener<'rowClick'> = (params) => {
    const row = params.row;

    // rejected accident can be removed
    if (row?.type === StatementRowType.ACCIDENT && row.isRejected) {
      setHideStatementDialogId(row.id);
      return;
    }

    // navigate to accident
    if (row?.type === StatementRowType.ACCIDENT && row.isProcessing) {
      navigate(PATHS.ACCIDENTS(row.id));
      return;
    }

    if (row.type === StatementRowType.CUSTOM && user) {
      setStatementDialogData({
        id: row.id,
        dealerId: Number.parseInt(user?.id),
        datetime: new Date(row.date),
        note: row.note,
        privateKm: row.privateKm,
        type: row.customType
      });
      setOpenStatementDialog(true);
    } else if (!row.isProcessing) {
      setRejectDealerStatementId(row.id);
      setIsRejectedByDealerStatement(row.isDealerStatementRejected);
    }
  };

  return (
    <PageLayout
      title="Výkaz obchodníka"
      headerBreakpoint="xl"
      extra={
        <Stack direction={{ xs: 'column', sm: 'row' }} flexWrap="wrap" alignItems={{ sm: 'stretch' }} gap={2}>
          <DateRangeToolbar
            value={dateRange}
            onChange={(r) => {
              const [f, t] = r;
              setCustom(new Date(f), new Date(t));
            }}
            weekStartsOn={1}
          />
          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{ minHeight: 56, paddingX: 3, whiteSpace: 'nowrap', flexShrink: 0 }}
            onClick={() => setOpenStatementDialog(true)}
            startIcon={<PlusCircle />}
          >
            Přidat záznam
          </Button>
        </Stack>
      }
    >
      <Grid container spacing={2}>
        <TotalItem
          value={dealerStatementReport?.statementsTotals?.countIsWaiting ?? 0}
          title="Čekající"
          isLoading={isLoadingData}
        />
        <TotalItem
          value={dealerStatementReport?.statementsTotals?.countInsurancePov ?? 0}
          title="Vyšlé z POV"
          isLoading={isLoadingData}
        />
        <TotalItem
          value={dealerStatementReport?.statementsTotals?.countInsuranceHav ?? 0}
          title="Vyšlé z HAV"
          isLoading={isLoadingData}
        />
        <TotalItem
          value={`${dealerStatementReport?.statementsTotals?.ratioSuccess ?? 0} %`}
          title="Úspěšnost"
          isLoading={isLoadingData}
        />
        <TotalItem
          value={`${dealerStatementReport?.statementsTotals?.vacationDays ?? 0} / ${dealerStatementReport?.statementsTotals?.paragraphDays ?? 0} / ${dealerStatementReport?.statementsTotals?.sickDays ?? 0}`}
          title="Dovolená / Paragraf / SickDay"
          isLoading={isLoadingData}
        />
        <TotalItem
          value={dealerStatementReport?.statementsTotals?.privateKm ?? 0}
          title="Privátní km"
          isLoading={isLoadingData}
        />
      </Grid>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', lg: 'center' }}
        gap={2}
        mt={1}
      >
        <Stack direction="row" flexWrap="wrap" columnGap={{ xs: 2, md: 3 }} rowGap={1.5} sx={{ ...surfaceSx }} p={2}>
          {statementTypes?.map((statement) => (
            <Stack key={statement.label} direction="row" gap={1} alignItems="center">
              <Box
                sx={{
                  height: 16,
                  width: 16,
                  borderRadius: 1,
                  backgroundColor: statement.color,
                  boxShadow: 'inset 0 0 0 1px rgba(34, 34, 52, 0.16)'
                }}
              />
              <Typography variant="body2" fontWeight={500}>
                {statement.label}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          flexWrap="wrap"
          alignItems={{ sm: 'center' }}
          gap={1.5}
          sx={{ ...surfaceSx, minWidth: 0 }}
          p={2}
        >
          <Typography variant="body2" fontWeight="600" color="text.secondary" whiteSpace="nowrap">
            Průměrná cena PHM
          </Typography>
          {!isSingleMonth ? (
            <Typography variant="body2" color="text.secondary">
              Vyberte jeden měsíc
            </Typography>
          ) : (
            <>
              <TextField
                size="small"
                value={phmPriceValue}
                onChange={(e) => setPhmPriceValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSavePhmPrice();
                  }
                }}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">Kč</InputAdornment>
                  },
                  htmlInput: { inputMode: 'decimal' }
                }}
                sx={{ width: 130 }}
                disabled={isSavingPhmPrice}
              />
              <Typography variant="body2" fontWeight="600" color="text.secondary" whiteSpace="nowrap">
                Litry PHM
              </Typography>
              <TextField
                size="small"
                value={phmLittersValue}
                onChange={(e) => setPhmLittersValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSavePhmPrice();
                  }
                }}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">l</InputAdornment>
                  },
                  htmlInput: { inputMode: 'decimal' }
                }}
                sx={{ width: 130 }}
                disabled={isSavingPhmPrice}
              />
              <Button
                size="small"
                variant="contained"
                color="primary"
                aria-label="Uložit PHM"
                sx={{ py: 1, minWidth: 44 }}
                onClick={handleSavePhmPrice}
                disabled={
                  phmPriceValue === '' ||
                  Number.isNaN(Number.parseFloat(phmPriceValue.replace(',', '.'))) ||
                  isSavingPhmPrice
                }
              >
                {isSavingPhmPrice ? <CircularProgress size={20} color="inherit" /> : <CheckIcon fontSize="small" />}
              </Button>
            </>
          )}
        </Stack>
      </Stack>
      <Box
        mt={1}
        sx={{
          ...surfaceSx,
          overflowX: 'auto',
          maxWidth: 1,
          width: 1,
          p: 2,
          '& .theme--White': {
            backgroundColor: '#fff'
          }
        }}
      >
        {/* Custom filtering */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} pb={2} pt={1} alignItems={{ md: 'center' }}>
          <Typography variant="body2" fontWeight="600" color="text.secondary">
            Rychlý filtr
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {[...STATUS_VALUES, ...PAYMENT_VALUES].map((s) => (
              <Chip
                key={s}
                label={s}
                clickable
                variant={selected.includes(s) ? 'filled' : 'outlined'}
                onClick={() => toggle(s)}
                color="secondary"
              />
            ))}
            {!!selected.length && (
              <Chip label="Zrušit filtr" onClick={clear} color="secondary" icon={<XClose size="14px" />} />
            )}
          </Stack>
        </Stack>

        <DataGrid
          apiRef={apiRef}
          columns={columns}
          rows={rows}
          loading={isLoadingData}
          autoHeight
          sx={{
            width: '100%'
          }}
          rowHeight={60}
          disableRowSelectionOnClick
          slots={{ toolbar: GridToolbar, panel: MobileGridPanel }}
          showCellVerticalBorder
          slotProps={{
            toolbar: {
              printOptions: { hideToolbar: true, hideFooter: true },
              showQuickFilter: true
            },
            panel: {
              sx: {
                '@media (max-width: 600px)': {
                  position: 'fixed !important',
                  top: 'auto !important',
                  right: '0 !important',
                  bottom: '0 !important',
                  left: '0 !important',
                  transform: 'none !important',
                  width: '100% !important',
                  maxWidth: '100% !important',
                  '& .MuiDataGrid-paper': {
                    width: '100%',
                    minWidth: '100%',
                    maxWidth: '100%',
                    maxHeight: '60vh',
                    borderTopLeftRadius: 12,
                    borderTopRightRadius: 12
                  }
                }
              }
            }
          }}
          getRowId={(row) => row.id}
          getRowClassName={(params) => `relative row-color-${params.row.id}`}
          onRowClick={handleRowClick}
          ignoreDiacritics
          onColumnVisibilityModelChange={onColumnVisibilityModelChange}
          onColumnWidthChange={onColumnWidthChange}
          onColumnOrderChange={onColumnOrderChange}
          {...(isPrefsReady && {
            initialState: {
              columns: {
                columnVisibilityModel: {
                  statementStatusLabel: false,
                  ...prefsInitialState.columns?.columnVisibilityModel,
                  [PAYMENT_FIELD]: false
                },
                orderedFields: prefsInitialState.columns?.orderedFields
              },
              density: prefsInitialState.density,
              sorting: prefsInitialState.sorting,
              filter: {
                filterModel: {
                  items: [],
                  quickFilterExcludeHiddenColumns: false
                }
              }
            }
          })}
        />
        <style>{rowStyles}</style>
        <style>{printStyles}</style>
      </Box>

      {user && !isEmpty(user) && (
        <AddNewStatementDialog
          open={openStatementDialog}
          defaultData={statementDialogData}
          onClose={handleCloseStatement}
          dealerId={Number.parseInt(user.id)}
          onSubmit={handleAddNewStatement}
          onUpdate={handleUpdateStatement}
          onRemove={handleRemoveStatement}
          isLoading={isSavingStatement || isRemovingStatement || isUpdatingStatement}
        />
      )}
      <ChangeAccidentStateDialog
        open={!!rejectDealerStatementId}
        onClose={() => setRejectDealerStatementId(undefined)}
        onAccept={(note) => handleRejectDealerStatement(note)}
        isLoading={isChangingStatementStatus || isHidingDealerStatementAccident}
        isRejected={
          dealerStatementReport?.results?.find((statement) => statement.id === rejectDealerStatementId)
            ?.isDealerStatementRejected
        }
        onRemove={() => {
          setHideStatementDialogId(rejectDealerStatementId);
        }}
        isDealerStatementRejected={isRejectedByDealerStatement}
      />
      <HideStatementDialog
        open={!!hideStatementDialogId}
        onClose={() => setHideStatementDialogId(undefined)}
        isLoading={isHidingDealerStatementAccident}
        onAccept={handleHideStatement}
      />
    </PageLayout>
  );
};

export const DealerStatementPage = () => {
  return <DealerStatementPageContent />;
};
